import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Table as TableIcon, AlertTriangle, CheckCircle2, ShieldAlert, BadgeAlert } from 'lucide-react';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { GenerateUsersMonthlyPayslips, MarkMonthlyPayslipsAsUnpaid, GetUsersMonthlyPayslips, MarkMonthlyPayslipsAsPaid, GetPayrollPreflight } from '../../../api/Payroll/monthlyPayroll';
import { MonthlyPayrollInput, MonthlyPayrollView, PayrollPreflightView } from '../../../models/payroll';
import { CompanyCurrencyRate, Country, Currency, Department, UserView } from '../../../models/general';
import { getCompanyRules, getDepartments } from '../../../api/General/Company';
import { getUsersCountries } from '../../../api/General/Country';
import { GetCompanyRates } from '../../../api/General/currency';
import { formatNumber } from '../../../utils';
import CurrencyDropdown from '../../../components/currencyDropdown';
import EmployeesList from '../../../components/employeesList';
import Pagination from '../../../components/Pagination';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';

const TablePayroll: React.FC = () => {
  const { t } = useTranslation();
  const userId = useSelector((s: any) => s.user.userId);
  const company = useSelector((s: any) => s.user.company);
  const companyCurrencyId = useSelector((s: any) => s.user.companyCurrencyId);
  const CompanyCurrencyRate = useSelector((s: any) => s.user.CompanyCurrencyRate);

  const { toast, showToast, dismissToast } = useToast();

  const PAGE_SIZE = 10;
  const [loading, setLoading] = useState(false);
  const [payslips, setPayslips] = useState<MonthlyPayrollView[]>([]);
  const [filtered, setFiltered] = useState<MonthlyPayrollView[]>([]);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [yearMonth, setYearMonth] = useState(new Date());
  const [departments, setDepartments] = useState<Department[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [companyCurrencyRates, setCompanyCurrencyRates] = useState<CompanyCurrencyRate[]>([]);
  const [, setBaseCurrency] = useState<Currency | null>(null);
  const [currency, setCurrency] = useState<any>(0);
  // Filters
  const [status, setStatus] = useState('All');
  const [employeeName, setEmployeeName] = useState('');
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [countryId, setCountryId] = useState<number>(0);
  const [position, setPosition] = useState('');
  const [minSalary, setMinSalary] = useState<number | ''>('');
  const [maxSalary, setMaxSalary] = useState<number | ''>('');
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Dialog — three-step flow ported from rocketnew /payroll-run-wizard:
  //   'select'   → pick employees
  //   'preflight'→ surface missing IBAN / labour card / pending approvals / already-paid
  //   'confirm'  → final go/no-go
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserView[]>([]);
  const [step, setStep] = useState<'select' | 'preflight' | 'confirm'>('select');
  const [preflight, setPreflight] = useState<PayrollPreflightView | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  const year = yearMonth.getFullYear();
  const month = yearMonth.getMonth() + 1;
  const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });

  // One-shot lookups: departments, countries, company currency rates, base currency.
  // These don't depend on the selected month/currency, so they load once per company and
  // don't re-fire when the user changes year/month or the currency dropdown.
  useEffect(() => {
    if (!company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [departmentsResp, countriesResp, ratesResp, rules] = await Promise.all([
          getDepartments(company.id),
          getUsersCountries(userId),
          GetCompanyRates(),
          getCompanyRules(company.id),
        ]);
        if (cancelled) return;
        setDepartments(departmentsResp.payload || []);
        setCountries(countriesResp.payload || []);
        const baseCurr: CompanyCurrencyRate = {
          id: 0,
          baseCurrency: rules.payload.currency,
          baseCurrencyId: rules.payload.currency.id,
          foreignCurrency: rules.payload.currency,
          foreignCurrencyId: 0,
          company: company,
          companyId: company.id,
          rate: 0,
        } as CompanyCurrencyRate;
        setBaseCurrency(rules.payload.currency);
        setCompanyCurrencyRates([baseCurr, ...(ratesResp.payload || [])]);
      } catch (e) {
        console.error(e);
        showToast(t('Failed to load payroll data'), 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [company, userId, t, showToast]);

  const loadPayslips = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      // Server scopes by loggedInUser.CompanyId — no need to send the full user-id list from the client.
      const payrollData = await GetUsersMonthlyPayslips({
        year, month,
        currencyId: currency?.foreignCurrencyId || companyCurrencyId,
      });
      setPayslips(payrollData.payload || []);
      setFiltered(payrollData.payload || []);
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load payroll data'), 'error');
    } finally {
      setLoading(false);
    }
  }, [company?.id, year, month, currency?.foreignCurrencyId, companyCurrencyId, t, showToast]);

  useEffect(() => { loadPayslips(); }, [loadPayslips]);

  const toggleRow = (id: number, userId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const allChecked = filtered.length > 0 && selectedIds.size === filtered.length;
  const indeterminate = selectedIds.size > 0 && selectedIds.size < filtered.length;
  const toggleAll = () => {
    if (allChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(f => f.id)));
  };

  const handleYearMonthChange = (date: Date) => {
    setYearMonth(date);
  };

  const getYearMonthPickerFromMonth = () => {
    const d = new Date(); d.setMonth(0); d.setFullYear(new Date().getFullYear() - 8); return d;
  };

  const getYearMonthPickerToMonth = () => {
    const d = new Date(); d.setMonth(12); d.setFullYear(new Date().getFullYear() + 1); return d;
  };

  const markPaid = async () => {
    try {
      if (!selectedIds.size) return;
      await MarkMonthlyPayslipsAsPaid({ year, month, payslipIds: Array.from(selectedIds) } as MonthlyPayrollInput);
      showToast(t('Records marked as paid'), 'success');
      await loadPayslips();
    } catch {
      showToast(t('Action failed'), 'error');
    }
  };

  const markUnpaid = async () => {
    try {
      if (!selectedIds.size) return;
      await MarkMonthlyPayslipsAsUnpaid({ year, month, payslipIds: Array.from(selectedIds) } as MonthlyPayrollInput);
      showToast(t('Records marked as unpaid'), 'success');
      await loadPayslips();
    } catch { showToast(t('Action failed'), 'error'); }
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleOpenMonthlyPay = () => { setStep('select'); setPreflight(null); setOpenDialog(true); };
  const handleCloseMonthlyPay = () => { setOpenDialog(false); setStep('select'); setPreflight(null); setSelectedUsers([]); };

  const runPreflight = async () => {
    if (!selectedUsers.length) return;
    setPreflightLoading(true);
    try {
      const resp = await GetPayrollPreflight({ year, month, userIds: selectedUsers.map(u => u.id) });
      setPreflight(resp.payload || null);
      setStep('preflight');
    } catch (e) {
      console.error('Preflight error:', e);
      showToast(t('Failed to run pre-flight check'), 'error');
    } finally {
      setPreflightLoading(false);
    }
  };

  const generateMonthlyPayroll = async (users: UserView[]) => {
    try {
      setLoading(true);
      await GenerateUsersMonthlyPayslips(users.map(u => u.id));
      showToast(t('Payroll generated'), 'success');
      await loadPayslips();
    } catch (e: any) {
      console.error('Generate monthly payroll error:', e);
      showToast(t('Generation failed') + ' ' + (e?.errors?.[0]?.description ?? ''), 'error');
    }
    finally { setLoading(false); handleCloseMonthlyPay(); }
  };

  useEffect(() => {
    const lowerName = employeeName.trim().toLowerCase();
    const lowerPos = position.trim().toLowerCase();
    const next = payslips.filter(p => {
      try {
        const fullName = (p.user.fullName || '').toString().toLowerCase();
        const title = (p.user.title || '').toString().toLowerCase();
        const statusMatch = status === 'All' || (status === 'Paid' && p.status) || (status === 'Unpaid' && !p.status);
        const nameMatch = !lowerName || fullName.includes(lowerName);
        const deptMatch = !departmentIds.length || departmentIds.includes(p.user.departmentId || 0);
        const countryMatch = countryId === 0 || p.user.countryId === countryId;
        const posMatch = !lowerPos || title.includes(lowerPos);
        const totalNet = Number(p.totalNetPay ?? 0);
        const minOk = !minSalary || totalNet >= +minSalary;
        const maxOk = !maxSalary || totalNet <= +maxSalary;
        return statusMatch && nameMatch && deptMatch && countryMatch && posMatch && minOk && maxOk;
      } catch (err) {
        console.error('Filter error for payslip row', p, err);
        return false;
      }
    });
    setFiltered(next); setPage(1); setSelectedIds(new Set());
  }, [status, employeeName, departmentIds, countryId, position, minSalary, maxSalary, payslips]);

  const resetFilters = () => {
    setStatus('All'); setEmployeeName(''); setDepartmentIds([]); setCountryId(0); setPosition(''); setMinSalary(''); setMaxSalary('');
  };

  const summary = useMemo(() => {
    return {
      total: filtered.reduce((a, c) => a + c.totalNetPay, 0),
      paid: filtered.filter(f => f.status).length,
      unpaid: filtered.filter(f => !f.status).length
    };
  }, [filtered]);

  const existingPayslipUserIds = useMemo(
    () => new Set(payslips.map(p => p.userId)),
    [payslips]
  );

  return (
    <PageLayout
      title={t('Payroll Table')}
      icon={TableIcon}
      accentColor="emerald"
      maxWidth="1600px"
      headerActions={
        <div className="flex flex-wrap items-center gap-2" data-testid="bulk-actions">
          <Button variant="outline" size="sm" onClick={markPaid} disabled={!selectedIds.size} data-testid="mark-paid-btn">{t('Mark Records As Paid')}</Button>
          <Button variant="outline" size="sm" onClick={markUnpaid} disabled={!selectedIds.size}>{t('Mark Records As Unpaid')}</Button>
          <Button variant="outline" size="sm" onClick={handleOpenMonthlyPay} iconName="Download" data-testid="generate-monthly-payroll-btn">{t('Generate Monthly Payroll')}</Button>
          <Button asChild variant="outline" size="sm" iconName="ExternalLink">
            <Link to="/payroll/wps-center" data-testid="wps-center-link">{t('WPS / Mudad')}</Link>
          </Button>
        </div>
      }
    >
    <div className="flex flex-col gap-5">
      {/* Toolbar — Filter / Currency / Year-Month picker on the left, summary
          badges on the right. Wraps to multiple lines only when the viewport
          can't fit them on one row. */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="payroll-toolbar">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowFilters(s => !s)}
            iconName={showFilters ? 'Minus' : 'Plus'}
            iconPosition="right"
          >
            {t('Filter')}
          </Button>
          <CurrencyDropdown
            value={currency}
            options={companyCurrencyRates}
            onOptionChange={(option: any) => setCurrency(option)}
          />
          <YearMonthPicker
            date={yearMonth}
            onChange={handleYearMonthChange}
            fromMonth={getYearMonthPickerFromMonth()}
            toMonth={getYearMonthPickerToMonth()}
            isRtl={false}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center rounded-full border border-border px-3 py-1 text-xs font-medium text-foreground">
            {t('Total')}: {formatNumber(summary.total)}
          </span>
          <span className="inline-flex items-center rounded-full border border-success/30 bg-success/5 px-3 py-1 text-xs font-medium text-success">
            {t('Paid')}: {summary.paid}
          </span>
          <span className="inline-flex items-center rounded-full border border-destructive/30 bg-destructive/5 px-3 py-1 text-xs font-medium text-destructive">
            {t('Unpaid')}: {summary.unpaid}
          </span>
          <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
            {t('Selected')}: {selectedIds.size}
          </span>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="rounded-lg border border-border p-4 flex flex-wrap gap-4">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-foreground">{t('Status')}</span>
            <div className="flex items-center gap-4">
              {(['All', 'Paid', 'Unpaid'] as const).map(val => (
                <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="payroll-status"
                    value={val}
                    checked={status === val}
                    onChange={() => setStatus(val)}
                    className="h-4 w-4 border-border text-primary focus:ring-primary accent-primary"
                  />
                  {t(val)}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-[160px]">
            <Input
              data-testid="employee-search"
              label={t('common:Employee')}
              value={employeeName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployeeName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[200px]">
            <Select
              multiple
              searchable
              data-testid="department-filter"
              options={departments.map(d => ({ value: d.id, label: d.name }))}
              value={departmentIds}
              onChange={setDepartmentIds}
              label={t('Department')}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[180px]">
            <Select
              searchable
              options={countries.map(c => ({ value: c.id, label: c.name }))}
              value={countryId || ''}
              onChange={(val: any) => setCountryId(val || 0)}
              label={t('Country')}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[160px]">
            <Input
              label={t('Position')}
              value={position}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPosition(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[140px]">
            <Input
              label={t('Minimum Salary')}
              type="number"
              value={minSalary}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinSalary(e.target.value ? +e.target.value : '')}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[140px]">
            <Input
              label={t('Maximum Salary')}
              type="number"
              value={maxSalary}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxSalary(e.target.value ? +e.target.value : '')}
            />
          </div>
          <div className="flex flex-col justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={resetFilters}>{t('Reset')}</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border overflow-x-auto" data-testid="payroll-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  indeterminate={indeterminate}
                  checked={allChecked}
                  onChange={toggleAll}
                />
              </TableHead>
              <TableHead>{t('Employee Name')}</TableHead>
              <TableHead className="text-end">{t('Gross Salary')}</TableHead>
              <TableHead className="text-end">{t('Variable Pay')}</TableHead>
              <TableHead className="text-end">{t('Adjustments')}</TableHead>
              <TableHead className="text-end" title={t('payroll.ot.tooltip', 'FDL 33/2021 Art. 19/20/21 — 1.25× weekday, 1.50× rest-day/holiday/night')}>
                {t('payroll.ot.header', 'OT')}
              </TableHead>
              <TableHead className="text-end">{t('Total Net Pay')}</TableHead>
              <TableHead className="text-end">{t('common:Status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <Spinner size="md" />
                </TableCell>
              </TableRow>
            )}
            {!loading && !paged.length && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm">
                  {t('No records')}
                </TableCell>
              </TableRow>
            )}
            {!loading && paged.map(row => {
              const currCode = currency?.foreignCurrency?.currencyCode || CompanyCurrencyRate?.currencyCode;
              const checked = selectedIds.has(row.id);
              return (
                <TableRow key={row.id}>
                  <TableCell className="w-10">
                    <Checkbox checked={checked} onChange={() => toggleRow(row.id, row.userId, !checked)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/profile/${row.userId}/payroll/active-payroll-month`} className="font-semibold text-primary hover:underline">
                        {row.user.fullName}
                      </Link>
                      {row.onProbation && row.probationExceeded && (
                        <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-semibold whitespace-nowrap">
                          {t('payroll.probation.exceeded', 'Probation exceeded')}
                        </span>
                      )}
                      {row.onProbation && !row.probationExceeded && (row.probationDays ?? 0) > 150 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold whitespace-nowrap">
                          {t('payroll.probation.dayLabel', 'Probation — {{days}}d', { days: row.probationDays })}
                        </span>
                      )}
                    </div>
                    <span className="block text-xs text-muted-foreground">{row.user.title}</span>
                  </TableCell>
                  <TableCell className="text-end">{formatNumber(row.grossSalary)} {currCode}</TableCell>
                  <TableCell className="text-end">{formatNumber(row.variablePay)} {currCode}</TableCell>
                  <TableCell className="text-end">{formatNumber(row.adjustments)} {currCode}</TableCell>
                  <TableCell className="text-end">
                    <div className="flex flex-col items-end">
                      <span>{formatNumber(Number(row.overtimePay || 0))} {currCode}</span>
                      {((row.regularOvertimeHours ?? 0) > 0 || (row.premiumOvertimeHours ?? 0) > 0) && (
                        <span className="text-[10px] text-muted-foreground">
                          {formatNumber(row.regularOvertimeHours ?? 0)}h · {formatNumber(row.premiumOvertimeHours ?? 0)}h*
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-end">{formatNumber(row.totalNetPay)} {currCode}</TableCell>
                  <TableCell className="text-end">
                    <StatusBadge variant={row.status ? 'success' : 'error'} size="sm">
                      {row.status ? t('PAID') : t('UNPAID')}
                    </StatusBadge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-end p-3">
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Generate Monthly Payroll Dialog — three-step flow (select → preflight → confirm) */}
      <Dialog open={openDialog} onOpenChange={(isOpen) => { if (!isOpen) handleCloseMonthlyPay(); }}>
          <DialogContent size="xl" className="flex flex-col max-w-3xl max-h-[85vh] overflow-y-hidden" data-testid="generate-payroll-dialog">
              <DialogHeader>
                  <DialogTitle>
                      {step === 'select' && t('Select Employees to Generate Payroll')}
                      {step === 'preflight' && t('Pre-Payroll Validation')}
                      {step === 'confirm' && t('Confirm Payroll Generation')}
                  </DialogTitle>
                  <DialogDescription>
                      <span className="inline-flex items-center gap-2">
                          <span>{monthName} {year}</span>
                          <span className="text-xs text-muted-foreground">
                              · {t('Step {{n}} of 3', { n: step === 'select' ? 1 : step === 'preflight' ? 2 : 3 })}
                          </span>
                      </span>
                  </DialogDescription>
              </DialogHeader>

              {step === 'select' && (
                  <>
                      <DialogBody className="flex-1 overflow-y-auto">
                          <EmployeesList
                              key={openDialog ? 'open' : 'closed'}
                              onSelectionChange={(users: UserView[]) => setSelectedUsers(users)}
                              existingPayslipUserIds={existingPayslipUserIds}
                          />
                      </DialogBody>
                      <DialogFooter>
                          <Button variant="outline" onClick={handleCloseMonthlyPay} data-testid="generate-cancel-btn">
                              {t('Cancel')}
                          </Button>
                          <Button
                              disabled={!selectedUsers.length || loading || preflightLoading}
                              loading={preflightLoading}
                              onClick={runPreflight}
                              data-testid="generate-continue-btn"
                          >
                              {t('Continue')} ({selectedUsers.length})
                          </Button>
                      </DialogFooter>
                  </>
              )}

              {step === 'preflight' && (
                  <>
                      <DialogBody className="flex-1 overflow-y-auto space-y-3" data-testid="preflight-panel">
                          <PreflightSection
                              icon={ShieldAlert}
                              tone={preflight && preflight.missingIbanCount > 0 ? 'error' : 'ok'}
                              title={t('Bank Details (IBAN)')}
                              count={preflight?.missingIbanCount ?? 0}
                              names={preflight?.missingIban.map(u => u.fullName) ?? []}
                              testId="preflight-iban"
                          />
                          <PreflightSection
                              icon={BadgeAlert}
                              tone={preflight && preflight.missingLabourCardCount > 0 ? 'error' : 'ok'}
                              title={t('Labour Card Number')}
                              count={preflight?.missingLabourCardCount ?? 0}
                              names={preflight?.missingLabourCard.map(u => u.fullName) ?? []}
                              testId="preflight-labour-card"
                          />
                          <PreflightSection
                              icon={AlertTriangle}
                              tone={preflight && preflight.alreadyPaidCount > 0 ? 'error' : 'ok'}
                              title={t('Already Paid for {{month}} {{year}}', { month: monthName, year })}
                              count={preflight?.alreadyPaidCount ?? 0}
                              names={preflight?.alreadyPaid.map(u => u.fullName) ?? []}
                              testId="preflight-already-paid"
                          />
                          <PreflightSection
                              icon={AlertTriangle}
                              tone={preflight && preflight.pendingApprovalsCount > 0 ? 'warn' : 'ok'}
                              title={t('Items Awaiting Approval')}
                              count={preflight?.pendingApprovalsCount ?? 0}
                              names={preflight?.pendingApprovals.map(a => `${a.fullName} (${a.pendingClaims + a.pendingAdjustments + a.pendingVariablePays})`) ?? []}
                              footnote={t('Pending claims, adjustments, or variable pays will not be included until approved.')}
                              testId="preflight-pending-approvals"
                          />
                      </DialogBody>
                      <DialogFooter>
                          <Button variant="outline" onClick={() => setStep('select')}>
                              {t('Back')}
                          </Button>
                          <Button
                              disabled={!preflight || preflight.hasBlockers || loading}
                              onClick={() => setStep('confirm')}
                              data-testid="preflight-continue-btn"
                          >
                              {preflight?.hasBlockers ? t('Resolve Blockers to Continue') : t('Continue')}
                          </Button>
                      </DialogFooter>
                  </>
              )}

              {step === 'confirm' && (
                  <>
                      <DialogBody className="space-y-4">
                          <p className="text-sm text-foreground">
                              {t('Generate payroll for {{count}} employees?', { count: selectedUsers.length })}
                          </p>
                          <p className="text-sm text-muted-foreground">
                              {monthName} {year}
                          </p>
                          {selectedUsers.filter(u => existingPayslipUserIds.has(u.id)).length > 0 && (
                              <p className="text-sm text-primary">
                                  {t('{{count}} already have payroll generated.', {
                                      count: selectedUsers.filter(u => existingPayslipUserIds.has(u.id)).length
                                  })}
                              </p>
                          )}
                      </DialogBody>
                      <DialogFooter>
                          <Button variant="outline" onClick={() => setStep('preflight')}>
                              {t('Back')}
                          </Button>
                          <Button
                              loading={loading}
                              disabled={loading}
                              onClick={() => generateMonthlyPayroll(selectedUsers)}
                              data-testid="generate-confirm-btn"
                          >
                              {t('Confirm')}
                          </Button>
                      </DialogFooter>
                  </>
              )}
          </DialogContent>
      </Dialog>

      {/* Toast */}
      <ToastRenderer toast={toast} onDismiss={dismissToast} />

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Spinner size="lg" />
        </div>
      )}
    </div>
    </PageLayout>
  );
};

type PreflightTone = 'ok' | 'warn' | 'error';

const toneClasses: Record<PreflightTone, { container: string; iconWrap: string; badge: string }> = {
  ok: {
    container: 'border-emerald-500/30 bg-emerald-500/5',
    iconWrap: 'text-emerald-600',
    badge: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  },
  warn: {
    container: 'border-amber-500/30 bg-amber-500/5',
    iconWrap: 'text-amber-600',
    badge: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  },
  error: {
    container: 'border-destructive/30 bg-destructive/5',
    iconWrap: 'text-destructive',
    badge: 'bg-destructive/10 text-destructive border-destructive/30',
  },
};

interface PreflightSectionProps {
  icon: React.ComponentType<{ className?: string; size?: number }>;
  tone: PreflightTone;
  title: string;
  count: number;
  names: string[];
  footnote?: string;
  testId?: string;
}

const PreflightSection: React.FC<PreflightSectionProps> = ({ icon: Icon, tone, title, count, names, footnote, testId }) => {
  const isOk = tone === 'ok' || count === 0;
  const DisplayIcon = isOk ? CheckCircle2 : Icon;
  const displayTone = isOk ? 'ok' : tone;
  const displayClasses = toneClasses[displayTone];
  return (
    <div className={`rounded-lg border p-3 ${displayClasses.container}`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <DisplayIcon className={`mt-0.5 ${displayClasses.iconWrap}`} size={20} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-sm text-foreground">{title}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${displayClasses.badge}`}>
              {isOk ? '✓' : count}
            </span>
          </div>
          {!isOk && names.length > 0 && (
            <ul className="mt-2 text-xs text-foreground/80 list-disc pl-4 space-y-0.5 max-h-28 overflow-y-auto">
              {names.slice(0, 10).map((n, i) => <li key={i}>{n}</li>)}
              {names.length > 10 && <li className="italic text-muted-foreground">…+{names.length - 10}</li>}
            </ul>
          )}
          {footnote && <p className="mt-2 text-xs text-muted-foreground">{footnote}</p>}
        </div>
      </div>
    </div>
  );
};

export default TablePayroll;
